import type { IMemoryId } from '@/service/agent';
import type { INoteAttachment, NoteType } from '@/service/note';

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_SHOPIFY {
  subTitle: string;
  link: string;
  pic: string;
  title: string;
  citationId?: string;
}

export enum WS_MSG_MULTIPLE_TEMPLATE_PARAMS_LEARN_MORE_TYPE {
  CITATION = 'CITATION',
  CITATION_NOTE = 'CITATION_NOTE'
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_BREAK_ICE_USERS {
  id: string;
  username: string;
  avatar?: string;
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_LEARN_MORE_ITEM {
  title: string;
  content: string;
  summary?: string;
  type: WS_MSG_MULTIPLE_TEMPLATE_PARAMS_LEARN_MORE_TYPE;
  index?: number;
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_SINGLE {
  link: string;
  citationId?: string;
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_LEARN_MORE_NOTE_ITEM
  extends WS_MSG_MULTIPLE_TEMPLATE_PARAMS_LEARN_MORE_ITEM {
  params: WS_MSG_MULTIPLE_TEMPLATE_PARAMS_NOTE;
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_LEARN_MORE {
  list: WS_MSG_MULTIPLE_TEMPLATE_PARAMS_LEARN_MORE_ITEM[];
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_NOTE {
  noteId: string;
  content: string;
  title?: string;
  type: NoteType;
  attachments?: INoteAttachment[];
  pictures?: string[];
  url: string;
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_SERENDIPITY_CARD {
  id: string;
  content: string;
  noteId?: string;
  insight: string;
  title?: string;
  attachments?: INoteAttachment[];
  createTime: string;
  feedback: string;
  subject?: string;
  subjectTitle?: string;
  suggests: string[];
  type?: string;
  updateTime: string;
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_CITATION_NOTE {
  noteId: string;
  content: string;
  title?: string;
  type: NoteType;
  attachments?: INoteAttachment[];
  pictures?: string[];
  citationId?: string;
  index?: number;
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_TODO {
  dataMap: {
    id: string;
    deadline: Date;
    content: string;
    status: string;
    detail?: string;
    reminderTime?: Date;
  };
  citationId: string;
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_MORE_RELATED_MEMORY {
  noteIds: string[];
  memoryIds: IMemoryId[];
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_RECOMMENDED_TOPIC {
  topics?: string[];
  serendipityId?: string;
}

export enum WS_MSG_MULTIPLE_TEMPLATE_PARAMS_CHAT_OVER_REASON {
  USER_END = 'USER_END',
  BOT_END = 'BOT_END'
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_MEBOT_CHAT_OVER {
  userId: string;
  userName: string;
  reason: WS_MSG_MULTIPLE_TEMPLATE_PARAMS_CHAT_OVER_REASON;
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_BREAK_ICE {
  sessionId: string;
  initiator: WS_MSG_MULTIPLE_TEMPLATE_PARAMS_BREAK_ICE_USERS;
  recipient: WS_MSG_MULTIPLE_TEMPLATE_PARAMS_BREAK_ICE_USERS;
  hookContent: string;
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_CIRCLE_INVITE {
  route: string;
  circleName: string;
  inviterUserName: string;
  inviterUserId: string;
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_SOFT_MEMORY {
  memoryIds: { id: string; type: string }[];
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_TALK {
  id: string;
}

export interface WS_MSG_MULTIPLE_TEMPLATE_PARAMS_DUET_SNAP {
  id: string;
  name: string;
}

export enum WS_MSG_MULTIPLE_TEMPLATE_TYPE {
  /* 商品卡片 */
  StandardCard = 'StandardCard',
  Single = 'Single',
  citation = 'citation',
  LearnMore = 'LearnMore',
  Note = 'Note',
  Todo = 'Todo',
  CitationNote = 'CitationNote',
  SerendipityCard = 'SerendipityCard',
  MoreRelatedMemory = 'NoteIds',
  RecommendedTopic = 'RecommendedTopic',
  MebotChatOver = 'MebotChatOver',
  SoftMemoryIds = 'SoftMemoryIds',
  Talk = 'Talk',
  DuetSnap = 'DuetSnap',
  BreakIce = 'BreakIce',
  CircleInvite = 'circleInvite'
}
export interface WS_MSG_MULTIPLE_TEMPLATE {
  templateName: WS_MSG_MULTIPLE_TEMPLATE_TYPE;
  source?: string;
  params:
    | WS_MSG_MULTIPLE_TEMPLATE_PARAMS_SHOPIFY
    | WS_MSG_MULTIPLE_TEMPLATE_PARAMS_SINGLE
    | WS_MSG_MULTIPLE_TEMPLATE_PARAMS_LEARN_MORE
    | WS_MSG_MULTIPLE_TEMPLATE_PARAMS_NOTE
    | WS_MSG_MULTIPLE_TEMPLATE_PARAMS_SERENDIPITY_CARD
    | WS_MSG_MULTIPLE_TEMPLATE_PARAMS_CITATION_NOTE
    | WS_MSG_MULTIPLE_TEMPLATE_PARAMS_TODO
    | WS_MSG_MULTIPLE_TEMPLATE_PARAMS_MORE_RELATED_MEMORY
    | WS_MSG_MULTIPLE_TEMPLATE_PARAMS_RECOMMENDED_TOPIC
    | WS_MSG_MULTIPLE_TEMPLATE_PARAMS_MEBOT_CHAT_OVER
    | WS_MSG_MULTIPLE_TEMPLATE_PARAMS_SOFT_MEMORY
    | WS_MSG_MULTIPLE_TEMPLATE_PARAMS_TALK
    | WS_MSG_MULTIPLE_TEMPLATE_PARAMS_DUET_SNAP;
}

export interface TemplateInterface {
  content: string;
}

export enum ChatSessionSubCodeAction {
  RESTART_SESSION = 'restartSession', // 红色，点击后重启 session
  RESEND_MSG = 'resendMessage', // 黄色，点击后重发当前消息
  SIMPLE_HINT = 'simpleHint', // 灰色系统消息，类似于 Genius Update
  OPEN_PAGE = 'openPage' // BYOK 等特定行为，用于页面的打开灯逻辑。
}

export enum SessionErrorCode {
  SESSION_CLOSED = 'session.status.closed',
  CONNECT_SESSION_TIMEOUT = 'connect session timeout',
  SESSION_OPENAI_ERROR = 'session.openai.error',
  SERVICE_STATUS_UNAVAILABLE = 'service.status.unavailable',
  SESSION_MESSAGE_EXCEED_REQUEST_LIMIT = 'session.message.exceedRequestLimit',
  PACKAGE_TURN_EXHAUSTION = 'package.turn.exhaustion',
  SESSION_MESSAGE_EXCEED_QPS_LIMIT = 'session.message.exceedQPSLimit',
  UMM_MSG_EXCEED_QPS_LIMIT = 'umm.msg.exceedQPSLimit',
  SESSION_RESPONSE_TIMEOUT = 'session.response.timeout',
  FREE_USED_UP_BYOK_PLAN = 'freeUsedUp.byokPlan',
  MESSAGE_LENGTH_LIMIT = 'message.length.limit'
}

export interface WS_SYSTEM_MULTIPLE_TEMPLATE_PARAMS_FRIEND_REQUEST_ACCEPT {
  initiator: {
    id: string;
    username: string;
  };
  recipient: {
    id: string;
    username: string;
  };
}

export interface WS_SYSTEM_MULTIPLE_TEMPLATE_PARAMS_CIRCLE_MEMBER_JOIN_MEMBER {
  id: string;
  route: string;
  username: string;
  avatar?: string;
}

export interface WS_SYSTEM_MULTIPLE_TEMPLATE_PARAMS_CIRCLE_MEMBER_JOIN {
  members: WS_SYSTEM_MULTIPLE_TEMPLATE_PARAMS_CIRCLE_MEMBER_JOIN_MEMBER[];
  inviter?: WS_SYSTEM_MULTIPLE_TEMPLATE_PARAMS_CIRCLE_MEMBER_JOIN_MEMBER;
}

export interface WS_SYSTEM_MULTIPLE_TEMPLATE_PARAMS_CIRCLE_COMMON_ACTION {
  action: string;
  targets: WS_SYSTEM_MULTIPLE_TEMPLATE_PARAMS_CIRCLE_MEMBER_JOIN_MEMBER[];
}

export enum WS_SYSTEM_MULTIPLE_TEMPLATE_TYPE {
  FriendRequestAccept = 'FriendRequestAccept',
  CircleMemberJoin = 'CircleMemberJoin',
  CircleOwnerTransfer = 'CircleOwnerTransfer',
  CircleAdminChange = 'CircleAdminChange',
  CircleMemberQuit = 'CircleMemberQuit'
}

export interface WS_SYSTEM_MULTIPLE_TEMPLATE {
  templateName: WS_SYSTEM_MULTIPLE_TEMPLATE_TYPE;
  source?: string;
  params: WS_SYSTEM_MULTIPLE_TEMPLATE_PARAMS_FRIEND_REQUEST_ACCEPT;
}
